import type { FC, ReactNode } from 'react'
import type { TinyHouseModel } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/generals/section-heading'
import { ModelCard } from '@/components/landing/model-card'
import { buttonVariants } from '@/components/ui/button'

interface FeaturedModelsProps {
    models: TinyHouseModel[]
}

export const FeaturedModels: FC<FeaturedModelsProps> = ({ models }): ReactNode => {
    return (
        <section id='models' className='border-t border-border/70 py-20 lg:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        eyebrow="Our models"
                        title="Find the home that fits your life"
                        description="Three proven designs, each a starting point. Pick a size, then make it yours with your own layout, materials and options."
                    />

                    <Link
                        href='/models#compare'
                        className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'self-start lg:self-auto')}
                    >
                        Compare all models
                        <ArrowRight className='size-4' />
                    </Link>
                </div>

                {models.length > 0 ? (
                    <div className='mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
                        {models.map(model => (
                            <ModelCard key={model.id} model={model} />
                        ))}
                    </div>
                ) : (
                    <div className='mt-14 rounded-2xl border border-dashed border-border bg-card px-6 py-14 text-center'>
                        <p className='font-heading text-2xl'>
                            Our models are being updated
                        </p>

                        <p className='mx-auto mt-3 max-w-md text-muted-foreground'>
                            Tell us what you are looking for and we will send you the latest designs and prices.
                        </p>

                        <Link href='/contact' className={cn(buttonVariants(), 'mt-6')}>
                            Get in touch
                        </Link>
                    </div>
                )}
            </div>
        </section>
    )
}
